<script lang="ts">
	import { onMount } from 'svelte';
	import { _ } from 'svelte-i18n';
	import ThemeToggle from '$lib/components/ui/ThemeToggle.svelte';
	import LanguageSwitcher from '$lib/components/ui/LanguageSwitcher.svelte';
	import Button from '$lib/components/primitives/Button.svelte';
	import { handleAnchorClick } from '$lib/utils/smoothScroll';

	interface NavItem {
		label: string;
		href?: string;
		type?: 'dropdown';
		featureGroups?: ProductCategory[];
		platformsCategory?: ProductCategory;
		resourcesCategories?: ProductCategory[];
	}

	interface ProductItem {
		label: string;
		href: string;
		description: string;
		icon?: string;
	}

	interface ProductCategory {
		title: string;
		items: ProductItem[];
	}

	// State
	let mobileMenuOpen = $state(false);
	let openDropdownIndex = $state<number | null>(null);
	let scrolled = $state(false);
	let hoverTimeout: ReturnType<typeof setTimeout> | null = null;
	let isMouseInDropdownZone = $state(false);

	// Menu handlers
	function toggleMobileMenu() {
		mobileMenuOpen = !mobileMenuOpen;
		if (typeof document !== 'undefined') {
			document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
		}
	}

	function closeMobileMenu() {
		mobileMenuOpen = false;
		if (typeof document !== 'undefined') {
			document.body.style.overflow = '';
		}
	}

	function toggleDropdown(index: number) {
		openDropdownIndex = openDropdownIndex === index ? null : index;
	}

	function openDropdown(index: number) {
		if (hoverTimeout) {
			clearTimeout(hoverTimeout);
			hoverTimeout = null;
		}
		openDropdownIndex = index;
		isMouseInDropdownZone = true;
	}

	function closeDropdown() {
		// Only close if mouse is not in the dropdown zone
		if (!isMouseInDropdownZone) {
			hoverTimeout = setTimeout(() => {
				if (!isMouseInDropdownZone) {
					openDropdownIndex = null;
				}
				hoverTimeout = null;
			}, 100);
		}
	}

	function handleDropdownMouseEnter(index: number) {
		if (hoverTimeout) {
			clearTimeout(hoverTimeout);
			hoverTimeout = null;
		}
		isMouseInDropdownZone = true;
		openDropdownIndex = index;
	}

	function handleDropdownMouseLeave() {
		isMouseInDropdownZone = false;
		hoverTimeout = setTimeout(() => {
			if (!isMouseInDropdownZone) {
				openDropdownIndex = null;
			}
			hoverTimeout = null;
		}, 100);
	}

	function handleNavClick(event: MouseEvent) {
		handleAnchorClick(event);
		closeMobileMenu();
		openDropdownIndex = null;
	}

	// Navigation data — grouped by category
	const featureGroups: ProductCategory[] = $derived([
		{
			title: $_('header.nav.productMenu.groups.work'),
			items: [
				{ label: $_('header.nav.productMenu.platform.todos'),    href: '/features/todos', description: '' },
				{ label: $_('header.nav.productMenu.platform.tasks'),    href: '/features/tasks', description: '' },
				{ label: $_('header.nav.productMenu.platform.projectMgmt'), href: '/features/project-management', description: '' },
				{ label: $_('header.nav.productMenu.platform.clients'),  href: '/features/clients', description: '' },
			]
		},
		{
			title: $_('header.nav.productMenu.groups.docs'),
			items: [
				{ label: $_('header.nav.productMenu.platform.notes'), href: '/features/notes', description: '' },
				{ label: $_('header.nav.productMenu.platform.files'), href: '/features/files', description: '' },
			]
		},
		{
			title: $_('header.nav.productMenu.groups.team'),
			items: [
				{ label: $_('header.nav.productMenu.platform.teamSupport'),   href: '/features/team-support', description: '' },
				{ label: $_('header.nav.productMenu.platform.pulse'),     href: '/features/pulse', description: '' },
				{ label: $_('header.nav.productMenu.platform.communication'), href: '/features/communication', description: '' },
				{ label: $_('header.nav.productMenu.platform.people'),    href: '/features/people', description: '' },
				{ label: $_('header.nav.productMenu.platform.absence'),   href: '/features/absence', description: '' },
			]
		},
		{
			title: $_('header.nav.productMenu.groups.business'),
			items: [
				{ label: $_('header.nav.productMenu.platform.timeTracking'), href: '/features/time-tracking', description: '' },
				{ label: $_('header.nav.productMenu.platform.invoicing'),    href: '/features/invoicing', description: '' },
				{ label: $_('header.nav.productMenu.platform.finance'),      href: '/features/finance', description: '' },
				{ label: $_('header.nav.productMenu.platform.reports'),      href: '/features/reports', description: '' },
			]
		}
	]);

	const platformsCategory: ProductCategory = $derived({
		title: '',
		items: [
			{
				label: $_('header.nav.productMenu.platform.webApp'),
				href: '/products/web',
				description: $_('header.nav.productMenu.platform.webAppDesc')
			},
			{
				label: $_('header.nav.productMenu.tools.cli'),
				href: '/products/cli',
				description: $_('header.nav.productMenu.tools.cliDesc')
			},
			{
				label: $_('header.nav.productMenu.mobile.mobile'),
				href: '/products/mobile',
				description: $_('header.nav.productMenu.mobile.mobileDesc')
			},
			{
				label: $_('header.nav.productMenu.tools.extensions'),
				href: '/products/extensions',
				description: $_('header.nav.productMenu.tools.extensionsDesc')
			}
		]
	});

	// Resources menu with two sections: Company and Resources (Company on the left)
	const resourcesCategories: ProductCategory[] = $derived([
		{
			title: $_('header.nav.company'),
			items: [
				{
					label: $_('header.nav.companyMenu.about'),
					href: '/about',
					description: $_('header.nav.companyMenu.aboutDesc')
				},
				{
					label: $_('header.nav.companyMenu.team'),
					href: '/team',
					description: $_('header.nav.companyMenu.teamDesc')
				}
			]
		},
		{
			title: $_('header.nav.resources'),
			items: [
				{
					label: $_('header.nav.productMenu.resources.docs'),
					href: '/docs',
					description: $_('header.nav.productMenu.resources.docsDesc')
				},
				{
					label: $_('header.nav.productMenu.resources.gettingStarted'),
					href: '/getting-started',
					description: $_('header.nav.productMenu.resources.gettingStartedDesc')
				},
				{
					label: $_('header.nav.productMenu.tools.integrations'),
					href: '/integrations',
					description: $_('header.nav.productMenu.tools.integrationsDesc')
				},
				{
					label: 'FAQ',
					href: '/faq',
					description: 'Common questions answered'
				}
			]
		}
	]);

	const navItems: NavItem[] = $derived([
		{
			label: $_('header.nav.product'),
			type: 'dropdown',
			featureGroups: featureGroups,
			platformsCategory: platformsCategory
		},
		{
			label: $_('header.nav.resources'),
			type: 'dropdown',
			resourcesCategories: resourcesCategories
		},
		{ label: $_('header.nav.pricing'), href: '/pricing' }
	]);

	// Lifecycle
	onMount(() => {
		const handleScroll = () => {
			scrolled = window.scrollY > 20;
		};

		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as HTMLElement;
			if (openDropdownIndex !== null && !target.closest('.nav__item--dropdown')) {
				openDropdownIndex = null;
			}
		};

		const handleEscapeKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				openDropdownIndex = null;
				mobileMenuOpen = false;
				if (typeof document !== 'undefined') {
					document.body.style.overflow = '';
				}
			}
		};

		// Initialize scroll state immediately
		scrolled = window.scrollY > 20;

		window.addEventListener('scroll', handleScroll);
		window.addEventListener('click', handleClickOutside);
		window.addEventListener('keydown', handleEscapeKey);

		return () => {
			window.removeEventListener('scroll', handleScroll);
			window.removeEventListener('click', handleClickOutside);
			window.removeEventListener('keydown', handleEscapeKey);
			if (typeof document !== 'undefined') {
				document.body.style.overflow = '';
			}
		};
	});
</script>

<header class="header {scrolled ? 'header--scrolled' : ''}">
	<div class="header__container">
		<a href="/" class="header__logo" aria-label={$_('components.header.heyweekHome')}>
			<img src="/heyweek-logo.svg" alt="Heyweek" class="logo-image" width="120" height="36" />
			<span class="beta-badge">BETA</span>
		</a>

		<nav class="header__nav" aria-label={$_('components.header.mainNavigation')}>
			<ul class="nav__list">
				{#each navItems as item, index}
					<li
						class="nav__item {item.type === 'dropdown' ? 'nav__item--dropdown' : ''}"
						onmouseenter={item.type === 'dropdown' ? () => handleDropdownMouseEnter(index) : undefined}
						onmouseleave={item.type === 'dropdown' ? handleDropdownMouseLeave : undefined}
					>
						{#if item.type === 'dropdown'}
							<button
								class="nav__link nav__link--dropdown"
								onclick={() => toggleDropdown(index)}
								aria-expanded={openDropdownIndex === index}
								aria-haspopup="true"
							>
								{item.label}
								<svg class="dropdown-icon" viewBox="0 0 20 20" fill="currentColor">
									<path
										fill-rule="evenodd"
										d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
										clip-rule="evenodd"
									/>
								</svg>
							</button>
							{#if openDropdownIndex === index}
								<div
									class="dropdown-menu dropdown-menu--enhanced"
									role="region"
									aria-label={$_('components.header.dropdownMenu')}
									onmouseenter={() => handleDropdownMouseEnter(index)}
									onmouseleave={handleDropdownMouseLeave}
								>
									<!-- Product Dropdown -->
									{#if item.featureGroups && item.platformsCategory}
										<div class="dropdown-content dropdown-content--sections">
											<div class="dropdown-sections-wrapper">
												<!-- Features Section — grouped -->
												<div class="dropdown-section dropdown-section--features">
													<div class="dropdown-section__title">{$_('header.nav.productMenu.sectionTitles.features')}</div>
													<div class="dropdown-feature-groups">
														{#each item.featureGroups as group}
															<div class="dropdown-feature-group">
																<div class="dropdown-feature-group__title">{group.title}</div>
																{#each group.items as subItem}
																	<a
																		href={subItem.href}
																		class="dropdown-item dropdown-item--compact"
																		onclick={handleNavClick}
																	>
																		{subItem.label}
																	</a>
																{/each}
															</div>
														{/each}
													</div>
													<div class="dropdown-more-features">
														<a href="/features" class="dropdown-more-features__link" onclick={handleNavClick}>
															{$_('header.nav.productMenu.platform.viewAllFeatures')}
														</a>
													</div>
												</div>

												<!-- Platforms Section -->
												<div class="dropdown-section dropdown-section--platforms">
													<div class="dropdown-section__title">{$_('header.nav.productMenu.sectionTitles.platforms')}</div>
													<div class="dropdown-section__content">
														<div class="dropdown-category">
															<div class="dropdown-category__items">
																{#each item.platformsCategory.items as subItem}
																	<a
																		href={subItem.href}
																		class="dropdown-item dropdown-item--enhanced"
																		onclick={handleNavClick}
																	>
																		<div class="dropdown-item__content">
																			<div class="dropdown-item__title">{subItem.label}</div>
																			<div class="dropdown-item__description">
																				{subItem.description}
																			</div>
																		</div>
																	</a>
																{/each}
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
										<!-- Resources Dropdown -->
									{:else if item.resourcesCategories}
										<div class="dropdown-content dropdown-content--sections">
											<div class="dropdown-sections-wrapper">
												{#each item.resourcesCategories as category, catIndex}
													<div
														class="dropdown-section {catIndex === 0
															? 'dropdown-section--resources'
															: 'dropdown-section--company'}"
													>
														<div class="dropdown-section__title">{category.title}</div>
														<div class="dropdown-section__content">
															<div class="dropdown-category">
																<div class="dropdown-category__items">
																	{#each category.items as subItem}
																		<a
																			href={subItem.href}
																			class="dropdown-item dropdown-item--enhanced"
																			onclick={handleNavClick}
																		>
																			<div class="dropdown-item__content">
																				<div class="dropdown-item__title">{subItem.label}</div>
																				<div class="dropdown-item__description">
																					{subItem.description}
																				</div>
																			</div>
																		</a>
																	{/each}
																</div>
															</div>
														</div>
													</div>
												{/each}
											</div>
											<!-- Weekly Footer Section -->
											<div class="dropdown-footer">
												<a href="/weekly" class="dropdown-footer__link" onclick={handleNavClick}>
													<div class="dropdown-footer__content">
														<div class="dropdown-footer__icon">
															<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
																<rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
																<line x1="16" y1="2" x2="16" y2="6"></line>
																<line x1="8" y1="2" x2="8" y2="6"></line>
																<line x1="3" y1="10" x2="21" y2="10"></line>
															</svg>
														</div>
														<div class="dropdown-footer__text">
															<div class="dropdown-footer__title">{$_('header.nav.companyMenu.weekly')}</div>
															<div class="dropdown-footer__description">{$_('header.nav.companyMenu.weeklyDesc')}</div>
														</div>
														<svg class="dropdown-footer__arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
															<polyline points="9 18 15 12 9 6"></polyline>
														</svg>
													</div>
												</a>
											</div>
										</div>
									{/if}
								</div>
							{/if}
						{:else if item.href}
							<a href={item.href} class="nav__link" onclick={handleNavClick}>{item.label}</a>
						{/if}
					</li>
				{/each}
			</ul>
		</nav>

		<div class="header__actions">
			<!-- Desktop-only actions; on mobile these live inside the hamburger menu so the
			     top bar can't overflow (logo + hamburger only). -->
			<div class="header__desktop-actions">
				<LanguageSwitcher />
				<ThemeToggle />
				<Button variant="ghost" size="sm" href="https://auth.heyweek.com/u/login/">
					{$_('header.actions.signIn')}
				</Button>
				<Button
					variant="primary"
					size="sm"
					href="https://app.heyweek.com/?signup"
					class="header__cta"
				>
					{$_('header.actions.startFree')}
				</Button>
			</div>

			<button
				class="mobile-menu-toggle"
				onclick={toggleMobileMenu}
				aria-expanded={mobileMenuOpen}
				aria-label={$_('components.header.toggleNavigationMenu')}
			>
				<svg
					class="menu-icon"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
				>
					{#if mobileMenuOpen}
						<path d="M6 6L18 18M6 18L18 6" />
					{:else}
						<path d="M4 6h16M4 12h16M4 18h16" />
					{/if}
				</svg>
			</button>
		</div>
	</div>

	<!-- Mobile menu -->
	{#if mobileMenuOpen}
		<div class="mobile-menu">
			<nav class="mobile-menu__nav">
				<ul class="mobile-menu__list">
					{#each navItems as item}
						{#if item.type === 'dropdown'}
							<li class="mobile-menu__item mobile-menu__item--section">
								<div class="mobile-menu__section-title">{item.label}</div>

								{#if item.featureGroups && item.platformsCategory}
									<!-- Product Dropdown Mobile -->
									{#each item.featureGroups as group}
										<div class="mobile-menu__section-group">
											<div class="mobile-menu__section-group-title">{group.title}</div>
											<div class="mobile-menu__category">
												{#each group.items as subItem}
													<a
														href={subItem.href}
														class="mobile-menu__link mobile-menu__link--sub"
														onclick={handleNavClick}
													>
														{subItem.label}
													</a>
												{/each}
											</div>
										</div>
									{/each}

									<div class="mobile-menu__section-group">
										<div class="mobile-menu__section-group-title">{$_('header.nav.productMenu.sectionTitles.platforms')}</div>
										<div class="mobile-menu__category">
											{#each item.platformsCategory.items as subItem}
												<a
													href={subItem.href}
													class="mobile-menu__link mobile-menu__link--sub"
													onclick={handleNavClick}
												>
													{subItem.label}
												</a>
											{/each}
										</div>
									</div>
								{:else if item.resourcesCategories}
									<!-- Resources Dropdown Mobile -->
									{#each item.resourcesCategories as category}
										<div class="mobile-menu__section-group">
											<div class="mobile-menu__section-group-title">{category.title}</div>
											<div class="mobile-menu__category">
												{#each category.items as subItem}
													<a
														href={subItem.href}
														class="mobile-menu__link mobile-menu__link--sub"
														onclick={handleNavClick}
													>
														{subItem.label}
													</a>
												{/each}
											</div>
										</div>
									{/each}
								{/if}
							</li>
						{:else if item.href}
							<li class="mobile-menu__item">
								<a href={item.href} class="mobile-menu__link" onclick={handleNavClick}
									>{item.label}</a
								>
							</li>
						{/if}
					{/each}
					<li class="mobile-menu__item">
						<a href="https://auth.heyweek.com/u/login/" class="mobile-menu__link"
							>{$_('header.actions.signIn')}</a
						>
					</li>
					<li class="mobile-menu__item">
						<Button
							variant="primary"
							size="md"
							href="https://app.heyweek.com/?signup"
							class="mobile-cta"
						>
							{$_('hero.cta.primary')}
						</Button>
					</li>
				</ul>
			</nav>
		</div>
	{/if}
</header>

<style>
	.header {
		position: sticky;
		top: 0;
		z-index: var(--z-sticky);
		background: var(--bg-glass);
		backdrop-filter: saturate(180%) blur(var(--backdrop-blur-xl));
		border-bottom: 1px solid var(--border-glass);
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
		padding: 0;
		margin: 0;
		height: 52px;
		min-height: 52px;
		max-height: 52px;
		overflow: visible;
		contain: layout style;
		box-sizing: border-box;
	}

	.header--scrolled {
		background: hsl(from var(--bg-primary) h s l / 0.8);
		backdrop-filter: saturate(200%) blur(var(--backdrop-blur-2xl));
		box-shadow: var(--shadow-subtle);
		border-bottom-color: var(--border-light);
	}

	.header__container {
		display: flex;
		align-items: center;
		justify-content: center;
		height: 52px;
		gap: var(--space-6);
		padding: 0 var(--space-4);
		margin: 0;
		max-width: var(--container-max-width);
		margin-inline: auto;
	}

	/* Logo */
	.header__logo {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		text-decoration: none;
		color: var(--text-brand);
		font-weight: var(--font-weight-bold);
		font-size: var(--font-size-xl);
		flex: 0 0 auto;
		min-width: 150px;
	}

	.logo-image {
		height: 32px;
		width: auto;
	}

	.beta-badge {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 2px 6px;
		margin-left: 8px;
		font-size: 12px;
		font-weight: 600;
		letter-spacing: 0.5px;
		line-height: 1;
		color: var(--text-tertiary);
		background: var(--hover-overlay);
		border: 1px solid var(--border-glass);
		border-radius: 4px;
		user-select: none;
		pointer-events: none;
	}

	/* Navigation */
	.header__nav {
		flex: 1;
		display: flex;
		justify-content: center;
		align-items: center;

		@media (max-width: 768px) {
			display: none;
		}
	}

	.nav__list {
		display: flex;
		align-items: center;
		gap: var(--space-8);
		list-style: none;
		margin: 0;
		padding: 0;
	}

	.nav__item {
		position: relative;
		display: flex;
		align-items: center;
	}

	.nav__link {
		color: var(--text-secondary);
		text-decoration: none;
		font-weight: var(--font-weight-medium);
		transition: var(--transition-colors);
		padding: var(--space-2) var(--space-3);
		border-radius: var(--radius-md);
		display: flex;
		align-items: center;
		justify-content: center;
		gap: var(--space-1);
		background: transparent;
		border: none;
		cursor: pointer;
		font-family: inherit;
		font-size: 14px;
	}

	.nav__link:hover {
		color: var(--text-primary);
		background: var(--hover-overlay);
	}

	.nav__link--dropdown {
		position: relative;
	}

	.dropdown-icon {
		width: 16px;
		height: 16px;
		transition: transform 0.2s ease;
	}

	.nav__link--dropdown[aria-expanded='true'] .dropdown-icon {
		transform: rotate(180deg);
	}

	/* Enhanced Dropdown Menu - Linear Style */
	.dropdown-menu {
		position: absolute;
		top: 100%;
		left: 50%;
		transform: translateX(-50%);
		z-index: var(--z-dropdown);
		animation: dropdownSlide 0.15s cubic-bezier(0.16, 1, 0.3, 1);
		margin-top: 8px;
	}

	/* Invisible bridge to prevent accidental closure when moving diagonally */
	.dropdown-menu::before {
		content: '';
		position: absolute;
		top: -8px;
		left: 0;
		right: 0;
		height: 8px;
		background: transparent;
		pointer-events: auto;
	}

	.dropdown-menu--enhanced {
		width: max-content;
		min-width: 740px;
		max-width: min(90vw, 860px);
		padding: 0;
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: 12px;
		box-shadow:
			0 4px 6px -1px rgba(0, 0, 0, 0.03),
			0 12px 24px -4px rgba(0, 0, 0, 0.05),
			0 20px 40px -8px rgba(0, 0, 0, 0.08);
		overflow: hidden;
	}

	:global(.dark) .dropdown-menu--enhanced {
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		box-shadow:
			0 4px 6px -1px rgba(0, 0, 0, 0.2),
			0 12px 24px -4px rgba(0, 0, 0, 0.3),
			0 20px 40px -8px rgba(0, 0, 0, 0.4);
	}

	.dropdown-content {
		display: flex;
		flex-direction: column;
	}

	.dropdown-content--sections {
		display: flex;
		flex-direction: column;
		min-height: auto;
	}
	
	.dropdown-sections-wrapper {
		display: grid;
		grid-template-columns: 1fr 1fr;
	}

	.dropdown-section {
		padding: 20px 24px;
	}

	.dropdown-section--features {
		border-right: 1px solid var(--border-glass);
		background: var(--bg-primary);
	}

	.dropdown-section--platforms {
		background: var(--bg-primary);
	}

	.dropdown-section--company {
		border-right: 1px solid var(--border-glass);
		background: var(--bg-primary);
	}

	.dropdown-section--resources {
		background: var(--bg-primary);
	}

	:global(.dark) .dropdown-section--features {
		border-right: 1px solid var(--border-glass);
		background: var(--bg-primary);
	}

	:global(.dark) .dropdown-section--platforms {
		background: var(--bg-primary);
	}

	:global(.dark) .dropdown-section--company {
		border-right: 1px solid var(--border-glass);
		background: var(--bg-primary);
	}

	:global(.dark) .dropdown-section--resources {
		background: var(--bg-primary);
	}

	.dropdown-section__title {
		color: var(--text-tertiary);
		font-size: 12px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.6px;
		margin-bottom: 16px;
		padding: 0;
		border: none;
	}

	:global(.dark) .dropdown-section__title {
		color: var(--text-tertiary);
	}

	.dropdown-section__content {
		display: flex;
		flex-direction: column;
		gap: 20px;
	}

	.dropdown-category {
		display: flex;
		flex-direction: column;
		gap: 2px;
	}


	.dropdown-category__items {
		display: flex;
		flex-direction: column;
		gap: 2px;
	}

	.dropdown-item {
		display: block;
		padding: var(--space-3) var(--space-4);
		text-decoration: none;
		border-radius: 6px;
		transition: all 0.15s ease;
	}

	.dropdown-item--enhanced {
		display: block;
		padding: 8px;
		border-radius: 6px;
		transition: background 0.15s ease;
		text-decoration: none;
		cursor: pointer;
	}

	.dropdown-item:hover,
	.dropdown-item--enhanced:hover {
		background: var(--hover-overlay);
	}

	:global(.dark) .dropdown-item--enhanced:hover {
		background: var(--hover-overlay);
	}

	.dropdown-item__content {
		width: 100%;
	}

	.dropdown-item__title {
		color: var(--text-primary);
		font-weight: 500;
		font-size: 14px;
		line-height: 1.4;
		margin-bottom: 2px;
	}
	
	/* Weekly Footer Section */
	.dropdown-footer {
		margin: 0 24px 20px 24px;
		padding-top: 20px;
		border-top: 1px solid var(--border-light);
	}
	
	.dropdown-footer__link {
		display: block;
		text-decoration: none;
		padding: 14px 16px;
		border-radius: 8px;
		background: linear-gradient(135deg, rgba(255, 94, 126, 0.02) 0%, transparent 100%);
		border: 1px solid rgba(255, 94, 126, 0.06);
		transition: all 0.2s ease;
	}
	
	.dropdown-footer__link:hover {
		background: linear-gradient(135deg, rgba(255, 94, 126, 0.05) 0%, rgba(255, 94, 126, 0.02) 100%);
		border-color: rgba(255, 94, 126, 0.1);
		transform: translateY(-1px);
	}
	
	.dropdown-footer__content {
		display: flex;
		align-items: center;
		gap: 12px;
	}
	
	.dropdown-footer__icon {
		width: 36px;
		height: 36px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: linear-gradient(135deg, rgba(255, 94, 126, 0.08) 0%, rgba(255, 94, 126, 0.04) 100%);
		border-radius: 8px;
		flex-shrink: 0;
		color: var(--brand-primary);
	}
	
	.dropdown-footer__text {
		flex: 1;
	}
	
	.dropdown-footer__title {
		font-size: 14px;
		font-weight: 600;
		color: var(--text-primary);
		margin-bottom: 2px;
	}
	
	.dropdown-footer__description {
		font-size: 12px;
		color: var(--text-secondary);
		line-height: 1.4;
	}
	
	.dropdown-footer__arrow {
		color: var(--text-tertiary);
		transition: transform 0.2s ease;
	}
	
	.dropdown-footer__link:hover .dropdown-footer__arrow {
		transform: translateX(2px);
		color: var(--text-secondary);
	}

	:global(.dark) .dropdown-item__title {
		color: var(--text-primary);
	}

	.dropdown-item__description {
		color: var(--text-secondary);
		font-size: 13px;
		line-height: 1.5;
		white-space: normal;
	}

	:global(.dark) .dropdown-item__description {
		color: var(--text-secondary);
	}

	/* Feature groups inside the features column */
	.dropdown-feature-groups {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--space-4) var(--space-6);
		padding: 0 0 var(--space-4);
	}

	.dropdown-feature-group {
		display: flex;
		flex-direction: column;
		gap: 1px;
	}

	.dropdown-feature-group__title {
		font-size: 12px;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 0.07em;
		color: var(--text-tertiary);
		padding: 4px 8px 6px;
	}

	.dropdown-item--compact {
		display: block;
		padding: 5px 8px;
		border-radius: 5px;
		font-size: 13.5px;
		font-weight: 500;
		color: var(--text-secondary);
		text-decoration: none;
		transition: background 0.12s ease, color 0.12s ease;
		line-height: 1.4;
	}

	.dropdown-item--compact:hover {
		color: var(--text-primary);
		background: var(--hover-overlay);
	}

	.dropdown-more-features {
		padding: 8px 4px 0;
		border-top: 1px solid var(--border-light);
		margin-top: 4px;
	}

	.dropdown-more-features__link {
		display: inline-flex;
		align-items: center;
		font-size: 13px;
		color: var(--text-brand);
		text-decoration: none;
		font-weight: 500;
		transition: opacity 0.15s ease;
		padding: 4px 8px;
	}

	.dropdown-more-features__link:hover {
		opacity: 0.8;
	}

	@keyframes dropdownSlide {
		from {
			opacity: 0;
			transform: translateX(-50%) translateY(-4px) scale(0.96);
		}
		to {
			opacity: 1;
			transform: translateX(-50%) translateY(0) scale(1);
		}
	}

	/* Actions */
	.header__actions {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: var(--space-3);
		flex: 0 0 auto;
		min-width: 150px;
	}

	.header__desktop-actions {
		display: flex;
		align-items: center;
		gap: var(--space-3);
	}

	/* On mobile the desktop actions (language, theme, sign in, start free) move into the
	   hamburger menu, leaving only logo + hamburger so the top bar never overflows. */
	@media (max-width: 768px) {
		.header__actions {
			min-width: 0;
		}
		.header__desktop-actions {
			display: none;
		}
	}

	:global(.header__cta) {
		@media (max-width: 768px) {
			display: none;
		}
	}

	/* Mobile menu toggle */
	.mobile-menu-toggle {
		display: none;
		align-items: center;
		justify-content: center;
		width: 40px;
		height: 40px;
		padding: 0;
		background: transparent;
		border: 1px solid var(--border-light);
		border-radius: var(--radius-md);
		color: var(--text-primary);
		cursor: pointer;
		transition: var(--transition-colors);

		@media (max-width: 768px) {
			display: flex;
		}
	}

	.mobile-menu-toggle:hover {
		background: var(--hover-overlay);
		border-color: var(--border-default);
	}

	.menu-icon {
		width: 24px;
		height: 24px;
	}

	/* Mobile menu */
	.mobile-menu {
		position: fixed;
		top: 52px;
		left: 0;
		right: 0;
		background: hsl(from var(--bg-primary) h s l / 0.95);
		backdrop-filter: blur(12px);
		border-bottom: 1px solid var(--border-light);
		animation: slideDown 0.3s ease-out;
		z-index: var(--z-dropdown);
		/* Cap to the space below the header and let the menu scroll when the category
		   list is taller than the screen (body scroll is locked while it's open). */
		max-height: calc(100vh - 52px);
		max-height: calc(100dvh - 52px);
		overflow-y: auto;
		-webkit-overflow-scrolling: touch;
		overscroll-behavior: contain;
	}

	.mobile-menu__list {
		list-style: none;
		margin: 0;
		padding: var(--space-4);
	}

	.mobile-menu__item {
		margin-bottom: var(--space-2);
	}

	.mobile-menu__link {
		display: block;
		padding: var(--space-3) var(--space-4);
		color: var(--text-primary);
		text-decoration: none;
		font-weight: var(--font-weight-medium);
		border-radius: var(--radius-md);
		transition: var(--transition-colors);
	}

	.mobile-menu__link:hover {
		background: var(--hover-overlay);
	}

	.mobile-menu__item--section {
		margin-bottom: var(--space-4);
	}

	.mobile-menu__section-title {
		padding: var(--space-3) var(--space-4);
		color: var(--text-secondary);
		font-weight: var(--font-weight-semibold);
		font-size: var(--font-size-sm);
		text-transform: uppercase;
		letter-spacing: 0.05em;
	}

	.mobile-menu__category {
		margin-bottom: var(--space-4);
	}


	.mobile-menu__section-group {
		margin-bottom: var(--space-6);
	}

	.mobile-menu__section-group-title {
		padding: var(--space-3) var(--space-4);
		color: var(--text-primary);
		font-weight: var(--font-weight-semibold);
		font-size: var(--font-size-base);
		margin-bottom: var(--space-2);
	}

	.mobile-menu__link--sub {
		padding-left: var(--space-6);
		font-size: var(--font-size-base);
	}

	:global(.mobile-cta) {
		width: 100%;
	}

	/* Responsive Design */
	@media (max-width: 1200px) {
		.dropdown-menu--enhanced {
			width: max-content;
			min-width: min(680px, calc(100vw - 32px));
			max-width: calc(100vw - 32px);
		}

		.dropdown-sections-wrapper {
			grid-template-columns: 1fr 1fr;
		}
	}

	@media (max-width: 768px) {
		.dropdown-menu--enhanced {
			width: auto;
			min-width: 320px;
			max-width: calc(100vw - 32px);
		}

		.dropdown-sections-wrapper {
			grid-template-columns: 1fr;
		}

		.dropdown-section {
			padding: 16px 20px;
		}
		
		.dropdown-footer {
			margin: 0 20px 16px 20px;
			padding-top: 16px;
		}
		
		.dropdown-footer__link {
			padding: 12px 14px;
		}

		.dropdown-section--features,
		.dropdown-section--platforms,
		.dropdown-section--company,
		.dropdown-section--resources {
			grid-column: span 1;
			border-right: none;
			border-bottom: 1px solid var(--border-glass);
		}

		.dropdown-section--platforms:last-child,
		.dropdown-section--resources:last-child {
			border-bottom: none;
		}

		:global(.dark) .dropdown-section--features,
		:global(.dark) .dropdown-section--platforms,
		:global(.dark) .dropdown-section--company,
		:global(.dark) .dropdown-section--resources {
			border-bottom: 1px solid var(--border-glass);
		}

		:global(.dark) .dropdown-section--platforms:last-child,
		:global(.dark) .dropdown-section--resources:last-child {
			border-bottom: none;
		}

		.dropdown-section__content {
			gap: 16px;
		}
	}

	@media (max-width: 480px) {
		.dropdown-menu--enhanced {
			left: 16px;
			right: 16px;
			width: auto;
			transform: none;
		}
	}

	/* Animations */
	@keyframes slideDown {
		from {
			opacity: 0;
			transform: translateY(-10px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}
</style>
